import type { DragEventHandler, RefObject } from "react"
import { useEffect, useRef } from "react"
import type { DragPreviewRenderer } from "react-aria"
import { useDrag, useDrop } from "react-aria"
import { holdSlotOnLine } from "./hold-slot-on-line"
import type { LessonDropTarget } from "./lesson-drag-session"
import {
  LESSON_DRAG_TYPE,
  anchorAfterDrop,
  clearPostDropAnchor,
  endDrag,
  pendingPostDropAnchor,
  session,
  startDrag,
  type LessonDragSource,
} from "./lesson-drag-session"

/**
 * Commits an accepted lesson move — the container's materialize-then-dispatch. The drop
 * hook confirms acceptance and resolves the in-flight `source` before calling it.
 */
export type CommitLessonMove = (source: LessonDragSource, target: LessonDropTarget) => void

/**
 * Makes a lesson draggable via react-aria `useDrag`. The grip is the single drag
 * affordance — the mouse/touch pad (`dragProps`) and the keyboard/SR button
 * (`dragButtonProps`); the title bar does not drag. The drag carries only the type tag —
 * the real source rides {@link startDrag} into the module session. The visible ghost is the
 * follower card (see use-lesson-drag-ghost.tsx), torn down here in `onDragEnd`.
 */
export function useLessonDragSource(
  source: LessonDragSource,
  canEdit: boolean,
  ghost?: {
    onDragEnd: () => void
  },
): {
  dragProps: ReturnType<typeof useDrag>["dragProps"]
  dragButtonProps: ReturnType<typeof useDrag>["dragButtonProps"]
  isDragging: boolean
  previewRef: RefObject<DragPreviewRenderer | null>
} {
  const previewRef = useRef<DragPreviewRenderer | null>(null)
  const { dragProps, dragButtonProps, isDragging } = useDrag({
    getItems: () => {
      const editor = source.getEditor()
      return [
        {
          [LESSON_DRAG_TYPE]: "",
          ...(editor && {
            "text/html": editor.getHTML(),
            "text/plain": editor.getText(),
          }),
        },
      ]
    },
    onDragStart: () => startDrag(source),
    onDragEnd: () => {
      endDrag()
      ghost?.onDragEnd()
    },
    // MOVE-only: otherwise react-aria allows every operation and the browser paints the
    // copy cursor (the green "+") over another course's slot.
    getAllowedDropOperations: () => ["move"],
    isDisabled: !canEdit,
    hasDragButton: true,
    preview: previewRef,
  })
  return { dragProps, dragButtonProps, isDragging, previewRef }
}

/**
 * Keeps the dropped-on slot on the exact viewport line the user dropped it on, so the lesson
 * lands where they aimed and the planner expands around it. The hold itself lives in
 * {@link holdSlotOnLine}; ending the drag re-expands every lesson on screen over 200ms (see
 * .bodyCollapser), which is exactly the reflow it is built to ride out.
 *
 * The watch is armed from module state ({@link pendingPostDropAnchor}) rather than from the drop
 * handler directly, because the planner may replace the slot's DOM wholesale and remount this
 * island between the drop and the reflow; the line is a viewport coordinate, so a remounted slot
 * picks the hold straight back up.
 */
function usePostDropAnchor(
  slotRef: RefObject<HTMLElement | null>,
  courseId: string,
  dateString: string,
): void {
  // A tracked-render read: re-renders the caller when a drop sets or clears the anchor.
  const anchor = pendingPostDropAnchor()
  const isAnchored = anchor?.courseId === courseId && anchor?.dateString === dateString
  const viewportTop = anchor?.viewportTop

  useEffect(() => {
    const slot = slotRef.current
    if (!isAnchored || viewportTop === undefined || !slot) return
    return holdSlotOnLine(slot, () => viewportTop, clearPostDropAnchor)
  }, [isAnchored, viewportTop, slotRef])
}

/**
 * Makes a course-date slot a drop target — pure browser mechanics, no persistence. Wraps
 * react-aria `useDrop`: it accepts a `move` only when a lesson drag is in flight, the type tag
 * matches, the drop is not onto the dragged lesson's own slot, and the target date is on. On an
 * accepted drop it calls {@link CommitLessonMove} (the container's materialize-then-dispatch)
 * and pins the slot to the viewport line it was dropped on.
 */
export function useLessonDropTarget(
  target: LessonDropTarget,
  ref: RefObject<HTMLElement | null>,
  onCommitMove: CommitLessonMove,
): {
  dropProps: ReturnType<typeof useDrop>["dropProps"]
  isDropTarget: boolean
} {
  // Whether this slot accepts the in-flight lesson drag: a lesson drag exists, the drop
  // isn't onto the dragged lesson's own slot, and the target date is on.
  const acceptsCurrentDrag = () => {
    const current = session()
    return (
      current.kind === "dragging" &&
      current.source.lesson.id !== target.courseDate.cardStackId &&
      !target.courseDate.isOff
    )
  }

  const { dropProps, isDropTarget } = useDrop({
    ref,
    getDropOperation: (types) =>
      types.has(LESSON_DRAG_TYPE) && acceptsCurrentDrag() ? "move" : "cancel",
    onDrop: () => {
      // Re-checked here, not just in getDropOperation: the browser refusing drops on
      // unaccepted targets is paint/gesture-layer behavior — the write path must not
      // depend on it.
      if (!acceptsCurrentDrag()) return
      const current = session()
      if (current.kind !== "dragging") return

      // Read the line the user dropped on BEFORE committing: the move's patches reflow the
      // planner, and after that this slot is no longer where they aimed.
      const viewportTop = ref.current?.getBoundingClientRect().top
      onCommitMove(current.source, target)
      if (viewportTop !== undefined) {
        anchorAfterDrop({
          courseId: target.course.id,
          dateString: target.courseDate.dateString,
          viewportTop,
        })
      }
    },
  })

  usePostDropAnchor(ref, target.course.id, target.courseDate.dateString)

  // Only call react-aria's drop handlers if we are actually dragging a lesson.
  // Otherwise, they swallow events generated while dragging editor content.
  // The keyboard drop path (registerDropTarget) is untouched.
  const gate =
    (handler: DragEventHandler | undefined): DragEventHandler =>
    (event) => {
      if (session().kind === "dragging") handler?.(event)
    }

  return {
    dropProps: {
      ...dropProps,
      onDragEnter: gate(dropProps.onDragEnter),
      onDragOver: gate(dropProps.onDragOver),
      onDragLeave: gate(dropProps.onDragLeave),
      onDrop: gate(dropProps.onDrop),
    },
    isDropTarget,
  }
}
